/**
 * 인사관리 > 직원 상세 페이지 (V55)
 *
 * V55 변경사항:
 * - 탭 구조 완전 제거 → 단일 페이지 스크롤 구조
 * - 직원 사진 영역 추가 (좌측 상단, 3x4 비율)
 * - 모든 섹션을 수직으로 배치
 *
 * **섹션 구성 (순서):**
 * 1. 인적사항 (Personal Info) - Read-only
 * 2. 조직/직무 (Organization) - 역할만 수정 가능
 * 3. 고용/계약 (Employment/Contract) - Read-only
 * 4. 학력/기타 (Education) - 완전 수정 가능
 * 5. 연락처 (Contact) - 완전 수정 가능
 *
 * **권한:**
 * - hr.manage 권한 필요 (HR, Super Admin만 접근 가능)
 * - 수정 권한도 hr.manage 기반으로 제어
 */

import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
} from 'lucide-react';
import { useEmployee } from '../../shared/hooks/useEmployees';
import {
  EMPLOYEE_STATUS,
  EMPLOYEE_STATUS_LABELS,
  EMPLOYEE_STATUS_COLORS,
  EmployeeStatus,
} from '../../shared/constants/employeeStatus';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { useEmployeeSensitiveInfo } from '../../shared/hooks/useEmployeeSensitiveInfo';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';

// V55: 섹션 컴포넌트
import { PersonalInfoTab } from './components/employeeDetail/PersonalInfoTab';
import { OrganizationTab } from './components/employeeDetail/OrganizationTab';
import { EmploymentContractTab } from './components/employeeDetail/EmploymentContractTab';
import { EducationTab } from './components/employeeDetail/EducationTab';
import { ContactAddressTab } from './components/employeeDetail/ContactAddressTab';
import { EmployeePhotoSection } from './components/employeeDetail/EmployeePhotoSection';


/**
 * 색상 이름을 CSS 변수로 매핑
 */
const COLOR_TO_CSS_VAR: Record<string, string> = {
  green: 'var(--color-success)',
  orange: 'var(--color-warning)',
  gray: 'var(--color-gray-400)',
};

/**
 * EmployeeDetailPageV19 컴포넌트
 */
export function EmployeeDetailPageV19() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // V62: 진입 source 기반 복귀 경로 (state 유실 시 기본 fallback)
  const backPath = (location.state as any)?.from || '/hr/employees';
  const { hasPermission, hasAnyRole } = useAuthorization();

  // V58: 현재 사용자 정보 (companyCode 스코프 체크용)
  const { user: currentUser } = useCurrentUser();

  // ========================================
  // 데이터 조회
  // ========================================

  // 직원 기본 정보
  const { employee, isLoading, error, refetch } = useEmployee(employeeId);

  // V98: 민감정보 조회 (주민번호/외국인번호 등)
  const { sensitiveInfo } = useEmployeeSensitiveInfo(employeeId);

  // ========================================
  // 권한 체크
  // ========================================

  /**
   * V58: 수정 권한 체크 (companyCode 스코프 적용)
   *
   * 조건:
   * 1. hr.manage 권한 필수
   * 2. system_admin 또는 role_global: 모든 회사 직원 수정 가능
   * 3. role_hr (일반 HR): 동일 companyCode 직원만 수정 가능
   */
  const canEdit = (() => {
    // 1. 기본 권한 체크
    if (!hasPermission('hr.manage')) {
      return false;
    }

    // 2. 글로벌 관리자: 모든 회사 수정 가능
    if (hasAnyRole(['system_admin', 'role_global'])) {
      return true;
    }

    // 3. 일반 HR: 동일 회사 직원만 수정 가능
    if (!employee || !currentUser) {
      return false;
    }

    const callerCompanyCode = currentUser.profile.companyCode;
    const targetCompanyCode = employee.companyCode;

    // companyCode가 없는 경우 수정 불가 (데이터 정합성 문제)
    if (!callerCompanyCode || !targetCompanyCode) {
      return false;
    }

    return callerCompanyCode === targetCompanyCode;
  })();

  // ========================================
  // 로딩 상태
  // ========================================

  if (isLoading) {
    return (
      <div>
        <BackButton onClick={() => navigate(backPath)} />
        <LoadingState message="직원 정보를 불러오는 중..." />
      </div>
    );
  }

  // ========================================
  // 에러 상태
  // ========================================

  if (error || !employee) {
    return (
      <div>
        <BackButton onClick={() => navigate(backPath)} />
        <ErrorState
          message={error ? '직원 정보 조회 중 오류가 발생했습니다' : '직원 정보를 찾을 수 없습니다'}
          detail={error ? error.message : '요청하신 직원의 정보가 존재하지 않거나 삭제되었습니다.'}
          onBack={() => navigate(backPath)}
        />
      </div>
    );
  }

  // ========================================
  // 렌더링
  // ========================================

  return (
    <div>
      {/* 뒤로가기 버튼 */}
      <BackButton onClick={() => navigate(backPath)} />

      {/* V55: 페이지 타이틀 */}
      <h1 style={pageTitleStyle}>직원 상세</h1>

      {/* V55: 상단 영역 (사진 + 기본 정보) */}
      <div style={topSectionStyle}>
        {/* 좌측: 사진 */}
        {/* V55.11: photoPath 사용 (getDownloadURL로 URL 획득) */}
        <EmployeePhotoSection
          employeeId={employee.id}
          photoPath={employee.photoPath}
          employeeName={employee.nameKo}
          canEdit={canEdit}
          onRefresh={refetch}
        />

        {/* 우측: 기본 정보 */}
        <div style={basicInfoStyle}>
          <div style={employeeNameStyle}>
            {employee.nameKo}
          </div>
          <div style={employeeMetaStyle}>
            <span style={employeeNumberStyle}>
              {employee.employeeNumber || '-'}
            </span>
            <StatusBadge status={employee.status as EmployeeStatus} />
          </div>
          {/* V55.3: 조직 한 줄, 직책/직위 한 줄 */}
          <div style={employeeOrgStyle}>
            {employee.orgUnitName || '-'}
          </div>
          <div style={employeePositionStyle}>
            {[employee.positionTitle, employee.jobTitle]
              .filter(Boolean)
              .join(' · ') || '-'}
          </div>
        </div>
      </div>

      {/* V55: 섹션들 (수직 스크롤) */}
      <div style={sectionsContainerStyle}>
        {/* 1. 인적사항 — V62: canEdit/onRefresh 전달 */}
        <PersonalInfoTab employee={employee} sensitiveInfo={sensitiveInfo} canEdit={canEdit} onRefresh={refetch} />

        {/* 2. 조직/직무 */}
        <OrganizationTab employee={employee} canEdit={canEdit} onRefresh={refetch} />

        {/* 3. 고용/계약 */}
        <EmploymentContractTab employee={employee} />

        {/* 4. 학력/기타 */}
        <EducationTab employee={employee} canEdit={canEdit} onRefresh={refetch} />

        {/* 5. 연락처 */}
        <ContactAddressTab employee={employee} canEdit={canEdit} onRefresh={refetch} />
      </div>
    </div>
  );
}

// ============================================
// 서브 컴포넌트
// ============================================

interface BackButtonProps {
  onClick: () => void;
}

function BackButton({ onClick }: BackButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        background: 'transparent',
        border: '1px solid var(--color-gray-300)',
        borderRadius: '6px',
        cursor: 'pointer',
        marginBottom: '24px',
        transition: 'all 0.2s ease',
      }}
    >
      <ArrowLeft size={18} />
      <span>목록으로</span>
    </button>
  );
}

interface LoadingStateProps {
  message: string;
}

function LoadingState({ message }: LoadingStateProps) {
  return (
    <div
      style={{
        padding: '60px 20px',
        textAlign: 'center',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <Loader
        size={40}
        style={{
          color: 'var(--color-primary)',
          animation: 'spin 1s linear infinite',
        }}
      />
      <h2 style={{ marginTop: '20px', color: 'var(--color-gray-700)' }}>{message}</h2>
    </div>
  );
}

interface ErrorStateProps {
  message: string;
  detail: string;
  onBack: () => void;
}

function ErrorState({ message, detail, onBack }: ErrorStateProps) {
  return (
    <div
      style={{
        padding: '60px 20px',
        textAlign: 'center',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid var(--color-error)',
      }}
    >
      <AlertCircle size={40} style={{ color: 'var(--color-error)' }} />
      <h2 style={{ marginTop: '20px', color: 'var(--color-error)' }}>{message}</h2>
      <p style={{ marginTop: '12px', color: 'var(--color-gray-600)' }}>{detail}</p>
      <button
        onClick={onBack}
        style={{
          marginTop: '20px',
          padding: '10px 20px',
          background: 'var(--color-primary)',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
        }}
      >
        목록으로 돌아가기
      </button>
    </div>
  );
}

interface StatusBadgeProps {
  status: EmployeeStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const safeStatus = status || EMPLOYEE_STATUS.ACTIVE;
  const colorName = EMPLOYEE_STATUS_COLORS[safeStatus];
  const cssColor = COLOR_TO_CSS_VAR[colorName];

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        background: `${cssColor}15`,
        color: cssColor,
      }}
    >
      {EMPLOYEE_STATUS_LABELS[safeStatus]}
    </span>
  );
}

// ============================================
// 스타일
// ============================================

const pageTitleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 'bold',
  color: 'var(--color-gray-800)',
  marginBottom: '24px',
};

const topSectionStyle: React.CSSProperties = {
  display: 'flex',
  gap: '24px',
  alignItems: 'flex-start',
  marginBottom: '32px',
  padding: '24px',
  background: 'white',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const basicInfoStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const employeeNameStyle: React.CSSProperties = {
  fontSize: '22px',
  fontWeight: 'bold',
  color: 'var(--color-gray-800)',
};

const employeeNameEnStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: '400',
  color: 'var(--color-gray-500)',
  marginLeft: '8px',
};

const employeeMetaStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
};

const employeeNumberStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '14px',
  color: 'var(--color-gray-600)',
};

const employeeOrgStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-600)',
  marginTop: '4px',
};

// V55.3: 직책/직위 스타일
const employeePositionStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-500)',
  marginTop: '2px',
};

const sectionsContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

// ============================================
// CSS 애니메이션 추가
// ============================================

const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('[data-v19-styles]')) {
  style.setAttribute('data-v19-styles', 'true');
  document.head.appendChild(style);
}
